/**
 * Created by alvaro on 4/10/17.
 */

namespace Application.Models {
    'use strict';

    const ENABLED_KEY: string = 'enabled';
    const CODEC_KEY: string = 'codec';
    const BITRATE_KEY: string = 'bitrate';
    const PID_KEY: string = 'pid';
    const L_LEVEL_KEY: string = 'lLevel';
    const R_LEVEL_KEY: string = 'rLevel';
    const L_LEVEL_DB_KEY: string = 'lLevelDB';
    const R_LEVEL_DB_KEY: string = 'rLevelDB';
    const INPUT_KEY: string = 'input';

    interface IDigestedInfo { [param: string]: string | number | boolean; }

    export interface IDeviceParams {
        id: string;
        lastUpdated: number;
        maintenance: boolean;
        name: string;
        params: IDeviceParam[];
        type: string;
    }

    export interface IDeviceParam {
        confirmed: boolean;
        deviceId: string;
        lastUpdate: string;
        lastValue: string | number | boolean;
        level: number;
        masked: boolean;
        name: string;
        type: string;
    }

    export interface IAudioInfo {
        enabled: boolean;
        lLevel: number;
        rLevel: number;
        lLevelDB: number;
        rLevelDB: number;
        codec: string;
        input: string;
        pid: number;
        bitrate: number;
    }

    export interface IEncoderOptions {
        encoderInput?: string;
        audioInput?: string;
        encoderOutput?: string;
        delay?: string;
        codec?: string;
        audioCodec?: string;
    }

    export interface IBlockDeviceParamMap {
        [index: number]: DeviceParam[];
    }
    
    export class DeviceParams implements IDeviceParams {
        public id: string;
        public lastUpdated: number;
        public maintenance: boolean;
        public name: string;
        public params: DeviceParam[];
        public type: string;
        private globalParams: DeviceParam[] = [];
        // private encoderBlocks: DeviceParam[] = [];
        // private decoderBlocks: DeviceParam[] = [];

        constructor(iDeviceParams: IDeviceParams) {
            this.id = iDeviceParams.id;
            this.lastUpdated = iDeviceParams.lastUpdated;
            this.maintenance = iDeviceParams.maintenance;
            this.name = iDeviceParams.name;
            this.params = iDeviceParams.params.map((iDeviceParam: IDeviceParam) => {
                return new DeviceParam(iDeviceParam);
            });
            this.type = iDeviceParams.type;
            // this.params.forEach((deviceParam: DeviceParam) => {
            //     if (deviceParam.name.indexOf("encoder[") === 0) {
            //         this.assignToBlockIndex(deviceParam, "encoder[", this.encoderBlocks);
            //     } else if (deviceParam.name.indexOf("decoder[") === 0) {
            //         this.assignToBlockIndex(deviceParam, "decoder[", this.decoderBlocks);
            //     } else {
            //         this.globalParams.push(deviceParam);
            //     }
            // });
        }

        public getAllGlobalParams(): DeviceParam[] {
            return this.globalParams;
        }

        public getAllParams(): DeviceParam[] {
            return this.params;
        }

        public getParam(name: string): DeviceParam {
            const [deviceParam]: DeviceParam[] = this.params.filter((deviceParam: DeviceParam) => {
                return deviceParam.name === name;
            });
            return deviceParam;

        }

        public getParamsBySuffix(suffix: string): DeviceParam[] {
            if (this.params !== undefined) {
                return this.params.filter((deviceParam: DeviceParam) => {
                    return deviceParam.name.indexOf(suffix) === 0;
                });
            }
        }

        public getDecoderBlockParams(suffix: string): DeviceParam[] {
            if (this.params !== undefined) {
                return this.params.filter((deviceParam: DeviceParam) => {
                    return deviceParam.name.indexOf(suffix) === 0;
                });
            }
        }

        public getEncoderInfo(): IDigestedInfo {
            let encoderInfo: IDigestedInfo = {};
            const allParams: DeviceParam[] = this.getAllParams();
            if (allParams !== undefined) {
                for (let p of allParams) {
                    encoderInfo[p.name] = p.lastValue;
                }
            }
            if (this.getAllGlobalParams() !== undefined) {
                for (let p of this.getAllGlobalParams()) {
                    encoderInfo[p.name] = p.lastValue;
                }
            }
            return encoderInfo;
        }

        public getDigesterInfo(): IDigestedInfo {
            let digesterInfo: IDigestedInfo = {};
            const allParams: DeviceParam[] = this.getAllParams();
            if (allParams !== undefined) {
                for (let p of allParams) {
                    digesterInfo[p.name] = p.lastValue;
                }
            }
            return digesterInfo;
        }

        public getAudioInfo(encoderInfo: IDigestedInfo, encoderOptions: IEncoderOptions): FullAudioInfo[] {
            let encoderAudios: FullAudioInfo[] = [];
            for (let i: number = 1; i <= 8; i++) {
                this.cleanUpOldValues(i, encoderInfo);
                let encoderAudioInfo: FullAudioInfo = FullAudioInfo.parseAudioInfo(encoderInfo, i, encoderOptions);
                if (encoderAudioInfo) {
                    encoderAudios.push(encoderAudioInfo);
                }

            }
            return encoderAudios;
        }

        private cleanUpOldValues(index: number, encoderInfo: IDigestedInfo) {
            const audioBase: string = 'audio[' + index + '].';
            this.params.filter((param: IDeviceParam) => param.name.indexOf(audioBase, 0) > -1)
                .filter((param: IDeviceParam) => !this.dataIsRecent(param.lastUpdate))
                .forEach((param: IDeviceParam) => {
                    if (encoderInfo[param.name]) {
                        delete encoderInfo[param.name];
                    }
                });
        }

        private dataIsRecent(lastUpdated: string): boolean {
            const parsedLastUpdated: moment.Moment = moment(lastUpdated);
            const now: moment.Moment = moment(new Date());
            return now.diff(parsedLastUpdated) < 2 * 60 * 1000;
        }

        // private assignToBlockIndex(deviceParam: Application.Models.DeviceParam, prefix: string,
        //                            map: IBlockDeviceParamMap): void {
        //     let endBlockIndex: number = deviceParam.name.indexOf("]");
        //     let blockIndex: number = +deviceParam.name.substring(
        //         prefix.length, endBlockIndex);
        //     if (map[blockIndex] === undefined) {
        //         map[blockIndex] = [];
        //     }
        //     map[blockIndex].push(deviceParam);
        // }

    }

    export class DeviceParam implements IDeviceParam {
        public confirmed: boolean;
        public deviceId: string;
        public lastUpdate: string;
        public lastValue: string | number | boolean;
        public level: number;
        public masked: boolean;
        public name: string;
        public type: string;

        constructor(iDeviceParam: IDeviceParam) {
            this.confirmed = iDeviceParam.confirmed;
            this.deviceId = iDeviceParam.deviceId;
            this.lastUpdate = iDeviceParam.lastUpdate;
            this.lastValue = iDeviceParam.lastValue;
            this.level = iDeviceParam.level;
            this.masked = iDeviceParam.masked;
            this.name = iDeviceParam.name;
            this.type = iDeviceParam.type;
        }
    }

    export class FullAudioInfo implements IAudioInfo {
        public infoString: string[];
        public someLeftLevel: boolean;

        public static parseAudioInfo(digestedInfo: IDigestedInfo,
                                     index: number,
                                     encoderOptions: IEncoderOptions): FullAudioInfo {
            const audioBase: string = "audio[" + index + "].";
            const audioBaseLength: number = audioBase.length;
            let audioInfo: IDigestedInfo = {};
            Object.keys(digestedInfo).forEach((key: string) => {
                if (key.indexOf(audioBase) >= 0) {
                    const property: string = key.substring(audioBaseLength);
                    audioInfo[property] = digestedInfo[key];
                }
            });
            if (encoderOptions) {
                let audioCodec: string = 'audioCodec';
                let audioInput: string = 'audioInput';
                if (encoderOptions[audioCodec]) {
                    audioInfo[CODEC_KEY] = encoderOptions[audioCodec];
                }
                if (encoderOptions[audioInput]) {
                    audioInfo[INPUT_KEY] = encoderOptions[audioInput];
                }
            }
            if (audioInfo[ENABLED_KEY]) {
                let lLevelDB2: number = parseInt(audioInfo[L_LEVEL_DB_KEY] as string, 10);
                let lLevel: number = parseInt(audioInfo[L_LEVEL_KEY] as string, 10);
                let rLevelDB2: number = parseInt(audioInfo[R_LEVEL_DB_KEY] as string, 10);
                let rLevel: number = parseInt(audioInfo[R_LEVEL_KEY] as string, 10);
                let fullAudioInfo: FullAudioInfo = new FullAudioInfo(true, audioInfo[CODEC_KEY] as string, audioInfo[BITRATE_KEY] as number,
                    audioInfo[PID_KEY] as number,
                    lLevel, rLevel,
                    audioInfo[INPUT_KEY] as string, lLevelDB2, rLevelDB2);
                return fullAudioInfo;
            } else {
                return undefined;
            }
        }

        constructor(public enabled: boolean,
                    public codec: string,
                    public bitrate: number,
                    public pid: number,
                    public lLevel: number,
                    public rLevel: number,
                    public input: string,
                    public lLevelDB: number,
                    public rLevelDB: number
        ) {
            this.infoString = this.getInfoString();
            this.someLeftLevel = (this.lLevel !== null && this.lLevel !== undefined)    // tslint:disable-line
                || (this.lLevelDB !== undefined && this.lLevelDB !== null) ;            // tslint:disable-line
        }

        public showAudioLevels(): boolean {
            return this.lLevel !== undefined && isNaN(this.lLevelDB);
        }

        private getInfoString(): string[] {
            if (this.codec === undefined && this.bitrate === undefined && this.pid === undefined) {
                return undefined;
            }
            let codec: string = this.codec ? this.codec : "";
            let audioBitrate: string = this.bitrate ? (this.bitrate / 1000 ).toFixed(2) + " kps" : "";
            let pidString: string = this.pid ? "PID " + this.pid : "";
            return [codec, audioBitrate, pidString];
        }

    }
}
